import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ICreateMerch,
  IMerchDetailResponse,
  IMerchFilteredListParams,
  IMerchPagesResponse,
  IMerchResponse,
  IUpdateMerch,
} from './types';

export const useGetMerchListInfinite = (params: IMerchFilteredListParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<IMerchResponse[], DefaultError>({
    queryKey: ['merch', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Merch/Search',
        params: {
          ...JSON.parse(JSON.stringify(params)),
          itemCount,
          page: pageParam,
        },
      });

      const value = await responseHandler<IMerchPagesResponse>(response, {
        groupName: `useGetMerchListInfinite - ${pageParam}`,
      });

      return infiniteHandler<IMerchResponse>(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetMerch = (id: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IMerchDetailResponse, DefaultError>({
    queryKey: ['merch', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Merch/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useGetMerch',
        notFoundPath: '/merch',
      });
    },
  });
};

export const usePostMerch = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePortMerch');
  return useMutation<IMerchResponse, DefaultError, ICreateMerch>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('merch.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'Merch',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePostMerch',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.name ?? i18next.t('merch.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useUpdateMerch = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePortMerch');
  return useMutation<IMerchResponse, DefaultError, IUpdateMerch>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('merch.what'),
        })
      );
      const response = await apiClient({ method: 'PUT', url: 'Merch', body });

      return responseHandler(response, {
        groupName: 'useUpdateMerch',
        notFoundPath: '/merch',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: data.name ?? i18next.t('merch.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteMerch = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteMerch');
  return useMutation<{}, DefaultError>({
    async mutationFn() {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('merch.what'),
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `Merch/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteMerch',
        notFoundPath: '/merch',
      });
    },

    onSuccess: data => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('merch.what'),
        })
      );
    },
    onError: dismiss,
  });
};
